import {
  DEFAULT_VIDEO_CHANNEL,
  VIDEO_CHANNEL_OPTIONS,
  VIDEO_CHANNEL_RESOLUTIONS,
  getVideoModelConfig,
  getVideoDurationOptions,
} from "./video/videoModels"
export {
  DEFAULT_VIDEO_CHANNEL,
  VIDEO_CHANNEL_OPTIONS,
  VIDEO_CHANNEL_RESOLUTIONS,
  getVideoDurationOptions,
} from "./video/videoModels"

export const VIDEO_TOOL_LABELS = {
  recreate: "微改编视频复刻",
  frameCopy: "高还原度视频复刻",
  oneClickCopy: "模板一键成片",
  origin: "自定义脚本成片",
} as const

export const VIDEO_TEXT_FIDELITY_WARNING =
  "Seedance 2.0 mini / Seedance 2.0 fast 模型对商品外观、包装文字还原能力有限，易出现文字错乱失真。若脚本镜头包含文字展示，建议选用 Seedance 2.0 标准 / Seedance 2.5，或在脚本中去掉文字展示类镜头"
export const FIRST_VIDEO_GIFT_POINTS = 50
export const DEFAULT_VIDEO_RESOLUTION = "720p" satisfies API.VideoResolution
export const DEFAULT_VIDEO_CHANNEL_RESOLUTIONS = VIDEO_CHANNEL_RESOLUTIONS[
  DEFAULT_VIDEO_CHANNEL
] ?? [DEFAULT_VIDEO_RESOLUTION]

export const getVideoResolutionOptions = (
  channel: API.Task.CreateVideoChannel,
) => {
  return VIDEO_CHANNEL_RESOLUTIONS[channel] ?? DEFAULT_VIDEO_CHANNEL_RESOLUTIONS
}

export const getVideoChannelLabel = (channel: API.Task.CreateVideoChannel) => {
  return (
    VIDEO_CHANNEL_OPTIONS.find((item) => item.value === channel) ??
    VIDEO_CHANNEL_OPTIONS.find((item) => item.value === DEFAULT_VIDEO_CHANNEL)!
  ).label
}

export const hasLimitedVideoTextFidelity = (
  channel: API.Task.CreateVideoChannel,
) => {
  return getVideoModelConfig(channel).limitedTextFidelity
}

export const getSafeVideoResolution = (
  channel: API.Task.CreateVideoChannel,
  resolution?: API.VideoResolution,
): API.VideoResolution => {
  const availableResolutions =
    VIDEO_CHANNEL_RESOLUTIONS[channel] ?? DEFAULT_VIDEO_CHANNEL_RESOLUTIONS

  if (resolution && availableResolutions.includes(resolution)) return resolution
  if (availableResolutions.includes(DEFAULT_VIDEO_RESOLUTION)) {
    return DEFAULT_VIDEO_RESOLUTION
  }

  return availableResolutions[0] as API.VideoResolution
}

export const POINT_ICON_URL =
  "https://img.alicdn.com/bao/uploaded/i1/2829330402/O1CN01G9unEm1EqAMf3CEal_!!4611686018427381730-2-item_pic.png"

export const RATIOS = Array.from(
  new Set(VIDEO_CHANNEL_OPTIONS.flatMap(({ ratios }) => ratios)),
)
export const DEFAULT_VIDEO_RATIO: API.VideoRatio = "9:16"

export const getVideoRatioOptions = (channel: API.Task.CreateVideoChannel) => {
  return getVideoModelConfig(channel).ratios
}

export const getSafeVideoRatio = (
  channel: API.Task.CreateVideoChannel,
  ratio?: API.VideoRatio,
): API.VideoRatio => {
  const availableRatios = getVideoRatioOptions(channel)

  if (ratio && availableRatios.includes(ratio)) return ratio
  if (availableRatios.includes(DEFAULT_VIDEO_RATIO)) {
    return DEFAULT_VIDEO_RATIO
  }

  return availableRatios[0]
}
export const FRAME_COPY_RESERVED_REFERENCE_IMAGE_COUNT = 3
export const getVideoReferenceImageLimit = (
  channel: API.Task.CreateVideoChannel,
) => {
  return getVideoModelConfig(channel).maxImageCount
}
export const getFrameCopyProductImageLimit = (
  channel: API.Task.CreateVideoChannel,
) => {
  return (
    getVideoReferenceImageLimit(channel) -
    FRAME_COPY_RESERVED_REFERENCE_IMAGE_COUNT
  )
}
export const getVideoDurationRange = (channel: API.Task.CreateVideoChannel) => {
  const durations = getVideoDurationOptions(channel)

  return { MIN: durations[0]!, MAX: durations.at(-1)! }
}
// 高还原度复刻在最后一步选择模型，前置素材按所有可用模型的支持范围校验。
export const FRAME_COPY_PRODUCT_IMAGE_LIMIT = Math.max(
  ...VIDEO_CHANNEL_OPTIONS.map(({ value }) =>
    getFrameCopyProductImageLimit(value),
  ),
)
export const FRAME_COPY_VIDEO_RANGE = {
  MIN: Math.min(
    ...VIDEO_CHANNEL_OPTIONS.map(
      ({ value }) => getVideoDurationRange(value).MIN,
    ),
  ),
  MAX: Math.max(
    ...VIDEO_CHANNEL_OPTIONS.map(
      ({ value }) => getVideoDurationRange(value).MAX,
    ),
  ),
} as const
export const getSafeVideoDuration = (
  channel: API.Task.CreateVideoChannel,
  value: number,
) => {
  const durations = getVideoDurationOptions(channel)

  return durations.find((duration) => duration >= value) || durations.at(-1)!
}
export const DEFAULT_VIDEO_DURATION = 8

export type VideoOptions = {
  duration: number
  model: API.Task.CreateVideoChannel
  ratio: API.VideoRatio
  resolution: API.VideoResolution
}

// 按模型能力修正时长、比例和分辨率
export const getSafeVideoOptions = <T extends VideoOptions>(options: T): T => ({
  ...options,
  duration: getSafeVideoDuration(options.model, options.duration),
  ratio: getSafeVideoRatio(options.model, options.ratio),
  resolution: getSafeVideoResolution(options.model, options.resolution),
})

// 时长超出模型范围时保留真实值供校验，不能静默截短或补长分镜
export const getSafeStoryboardDuration = (
  model: API.Task.CreateVideoChannel,
  duration: number,
) => {
  const { MIN, MAX } = getVideoDurationRange(model)
  if (duration < MIN || duration > MAX) return duration
  return getSafeVideoDuration(model, duration)
}

// 生成参数时长跟随最后一个分镜的结束时间
export const getStoryboardGenerationOptions = (
  videoOptions: VideoOptions,
  endTime: number | undefined,
): VideoOptions => {
  if (endTime === undefined || !Number.isFinite(endTime) || endTime <= 0) {
    return videoOptions
  }
  const duration = getSafeStoryboardDuration(videoOptions.model, endTime)
  return duration === videoOptions.duration
    ? videoOptions
    : { ...videoOptions, duration }
}

export const DEFAULT_VIDEO_OPTIONS: VideoOptions = {
  duration: DEFAULT_VIDEO_DURATION,
  model: DEFAULT_VIDEO_CHANNEL,
  ratio: DEFAULT_VIDEO_RATIO,
  resolution: DEFAULT_VIDEO_RESOLUTION,
}

export const API_ERROR_CODE = {
  UNAUTHORIZED: 100002,
} as const

export enum ApiSuccessFlag {
  failed = 0,
  succeeded = 1,
}

export enum VerifyCodeType {
  login = 1,
}

export enum UserMaterialSource {
  uploaded = 1,
  generated = 2,
}

export enum UserLevelType {
  free = 1,
  pro = 2,
}

export enum ProductCode {
  proMonthlyV1 = 1,
  proMonthlyGiftV1 = 50,
  walletProV1 = 100,
  walletFreeV1 = 101,
  bulkWalletV1 = 102,
}

export enum OrderType {
  levelPackage = 1,
  walletPackage = 2,
  bulkWalletPackage = 3,
}

export enum BulkWalletEntryMode {
  quantity = 1,
  amount = 2,
}

export enum OrderStatus {
  init = 1,
  waitPay = 2,
  cancelled = 3,
  closed = 4,
  succeeded = 5,
}

export enum OrderFulfillmentStatus {
  init = 1,
  scheduled = 2,
  fulfilling = 3,
  completed = 4,
  cancelled = 5,
}

export enum OrderSource {
  staffManual = 1,
  onlinePurchase = 2,
  staffGift = 3,
}

export enum PaymentMethod {
  wechat = 1,
  alipay = 2,
  staffGift = 3,
}

export enum PaymentTradeStatus {
  waitPay = 1,
  paid = 2,
  closed = 3,
}

export enum WalletRechargeType {
  registrationGift = 1,
  staff = 2,
  levelInclude = 3,
  walletPackage = 4,
}

export enum WalletRechargeStatus {
  scheduled = 1,
  active = 2,
  exhausted = 3,
  expired = 4,
}

export enum WalletRecordType {
  income = 1,
  expense = 2,
  expiredClear = 3,
}

export enum WalletConsumeSource {
  parseVideo = 1,
  generateVideo = 2,
  whiteBgImage = 3,
  nineGridImage = 4,
}

export enum TaskStepStatus {
  pending = 1,
  processing = 2,
  succeeded = 3,
  failed = 4,
}

export enum FrameCopyStatus {
  init = 1,
  blendFrame = 2,
  blendFrameFailed = 3,
  blendFrameDone = 4,
  blendPrompt = 5,
  blendPromptFailed = 6,
  blendPromptDone = 7,
  createVideo = 8,
  createVideoFailed = 9,
  createVideoDone = 10,
  copy = 11,
  parseDialog = 12,
  parseDialogDone = 13,
  parseDialogFailed = 14,
}

export enum ModelFigureType {
  system = 1,
  manual = 2,
}

export enum BlendFrameType {
  ai = 1,
  manual = 2,
}

export enum LegacyTaskStatus {
  processing = 0,
}

export enum ImageTaskStatus {
  pending = 1,
  created = 2,
  running = 3,
  succeeded = 4,
  failed = 5,
  cancelled = 6,
}

export enum HotVideoTaskStatus {
  init = 1,
  parseVideoProcessing = 2,
  parseVideoSucceeded = 3,
  optimizePromptProcessing = 4,
  optimizePromptSucceeded = 5,
  createVideoProcessing = 6,
  createVideoSucceeded = 7,
  parseVideoFailed = 12,
  optimizePromptFailed = 15,
  createVideoFailed = 16,
  replaceAudioProcessing = 17,
  replaceAudioSucceeded = 18,
  replaceAudioFailed = 19,
  hidden = 20,
}

export enum HotVideoAudioType {
  ai = 1,
  original = 2,
}

const PROCESSING_IMAGE_TASK_STATUSES = new Set<ImageTaskStatus>([
  ImageTaskStatus.pending,
  ImageTaskStatus.created,
  ImageTaskStatus.running,
])

const FAILED_IMAGE_TASK_STATUSES = new Set<ImageTaskStatus>([
  ImageTaskStatus.failed,
  ImageTaskStatus.cancelled,
])

const FAILED_HOT_VIDEO_TASK_STATUSES = new Set<HotVideoTaskStatus>([
  HotVideoTaskStatus.parseVideoFailed,
  HotVideoTaskStatus.optimizePromptFailed,
  HotVideoTaskStatus.createVideoFailed,
  HotVideoTaskStatus.replaceAudioFailed,
])

const PROCESSING_HOT_VIDEO_TASK_STATUSES = new Set<HotVideoTaskStatus>([
  HotVideoTaskStatus.init,
  HotVideoTaskStatus.parseVideoProcessing,
  HotVideoTaskStatus.optimizePromptProcessing,
  HotVideoTaskStatus.createVideoProcessing,
  HotVideoTaskStatus.replaceAudioProcessing,
])

const SUCCEEDED_HOT_VIDEO_TASK_STATUSES = new Set<HotVideoTaskStatus>([
  HotVideoTaskStatus.createVideoSucceeded,
  HotVideoTaskStatus.replaceAudioSucceeded,
])

export const isTaskStepProcessingStatus = (status: TaskStepStatus) => {
  return (
    status === TaskStepStatus.pending || status === TaskStepStatus.processing
  )
}

export const isTaskStepSucceededStatus = (status: TaskStepStatus) => {
  return status === TaskStepStatus.succeeded
}

export const isTaskStepFailedStatus = (status: TaskStepStatus) => {
  return status === TaskStepStatus.failed
}

export const isImageTaskProcessingStatus = (status: ImageTaskStatus) => {
  return PROCESSING_IMAGE_TASK_STATUSES.has(status)
}

export const isImageTaskSucceededStatus = (status: ImageTaskStatus) => {
  return status === ImageTaskStatus.succeeded
}

export const isImageTaskFailedStatus = (status: ImageTaskStatus) => {
  return FAILED_IMAGE_TASK_STATUSES.has(status)
}

export const isHotVideoTaskSucceededStatus = (status: HotVideoTaskStatus) => {
  return SUCCEEDED_HOT_VIDEO_TASK_STATUSES.has(status)
}

export const isHotVideoTaskFailedStatus = (status: HotVideoTaskStatus) => {
  return FAILED_HOT_VIDEO_TASK_STATUSES.has(status)
}

export const isHotVideoTaskProcessingStatus = (status: HotVideoTaskStatus) => {
  return PROCESSING_HOT_VIDEO_TASK_STATUSES.has(status)
}

export const MOBILE_PHONE_PATTERN = /^1[3-9]\d{9}$/
